import React, { useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import StyleSelector from '../shared/StyleSelector';
import './AppHomepage.css'; // Shared styles

const Sidebar = ({ isOpen, onClose, chapterPageStyle, onChapterPageStyleChange, chapterPageStyles }) => {
    const { userProfile, isAdmin, uiFont, setUiFontPreference, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { level } = useParams();
    const isAppHomepage = location.pathname === '/app-homepage';
    const isLevelPage = /^\/level\/\d+$/u.test(location.pathname);
    const isChapterPage = /^\/chapter-page\/\d+\/\d+$/u.test(location.pathname);
    const [localFont, setLocalFont] = useState(uiFont || 'bebas');
    const FONT_OPTIONS = [
        { value: 'bebas', label: 'Bebas Neue' },
        { value: 'rajdhani', label: 'Rajdhani' },
        { value: 'teko', label: 'Teko' },
        { value: 'inter', label: 'Inter' },
        { value: 'robotoCondensed', label: 'Roboto Condensed' }
    ];
    // Navigation handler
    const handleNavigation = (page) => {
        try {
            if (onClose) onClose();

            switch (page) {
                case 'dashboard':
                    navigate('/dashboard');
                    break;
                case 'profile':
                    // navigate('/profile'); // Future
                    console.log('Profile page not implemented yet');
                    break;
                case 'logout':
                    logout().then(() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        navigate('/', { replace: true });
                    }).catch((err) => {
                        console.error('Logout error:', err);
                        localStorage.clear();
                        sessionStorage.clear();
                        navigate('/', { replace: true });
                    });
                    break;
                case 'main-admin':
                    navigate('/admin/main');
                    break;
                default:
                    console.log('Unknown navigation target:', page);
            }
        } catch (error) {
            console.error('Navigation error:', error);
        }
    };

    return (
        <>
            {/* Mobile Overlay (Phase 4 Premium Refactor) */}
            <div
                className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            >
                <div className="sidebar-panel" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="sidebar-header">
                        <div className="sidebar-title">Settings</div>
                        <button className="close-sidebar-btn" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* Navigation Items */}
                    <div className="sidebar-nav-list">
                        {isChapterPage && level && (
                            <button
                                className="sidebar-nav-item"
                                onClick={() => { if (onClose) onClose(); navigate(`/level/${level}`); }}
                            >
                                <i className="fas fa-arrow-left"></i>
                                <span>Back to Level</span>
                            </button>
                        )}
                        <button className="sidebar-nav-item" onClick={() => handleNavigation('dashboard')}>
                            <i className="fas fa-home"></i>
                            <span>Dashboard</span>
                        </button>

                        <button className="sidebar-nav-item" onClick={() => handleNavigation('profile')}>
                            <i className="fas fa-user"></i>
                            <span>Profile</span>
                        </button>

                        {/* Admin Tools */}
                        {isAdmin && (
                            <>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', padding: '1rem 0.5rem 0.5rem', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    Admin
                                </div>
                                <button
                                    className="sidebar-nav-item"
                                    onClick={() => handleNavigation('main-admin')}
                                    style={{ color: 'var(--accent)' }}
                                >
                                    <i className="fas fa-rocket"></i>
                                    <span>Admin Portal</span>
                                </button>
                            </>
                        )}

                        <div style={{ height: '1px', background: 'var(--border-subtle)', margin: '1rem 0' }}></div>

                        <button className="sidebar-nav-item" onClick={() => handleNavigation('logout')}>
                            <i className="fas fa-sign-out-alt"></i>
                            <span>Log Out</span>
                        </button>

                        {/* Chapter Page style selector (in sidebar) */}
                        {isChapterPage && chapterPageStyle != null && onChapterPageStyleChange && chapterPageStyles?.length > 0 && (
                            <div style={{ marginTop: '1rem', padding: '0.5rem 0.25rem', borderTop: '1px solid var(--border-subtle)' }}>
                                <StyleSelector
                                    currentStyle={chapterPageStyle}
                                    onStyleSelect={onChapterPageStyleChange}
                                    styles={chapterPageStyles}
                                />
                            </div>
                        )}

                        {/* Admin-only font controls (all nav pages – font is site-wide via --font-family-base on :root) */}
                        {isAdmin && (isAppHomepage || isLevelPage || isChapterPage) && (
                            <div style={{ marginTop: '1rem', padding: '0.5rem 0.25rem', borderTop: '1px solid var(--border-subtle)' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.35rem' }}>
                                        Font (Site-wide)
                                </div>
                                <select
                                    value={localFont}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setLocalFont(next);
                                        // Live-apply to current page only (no persist)
                                        setUiFontPreference(next, { persist: false });
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '0.35rem 0.5rem',
                                        borderRadius: '4px',
                                        border: '1px solid var(--border-subtle)',
                                        background: 'var(--surface)',
                                        color: 'var(--text-primary)',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {FONT_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    className="sidebar-nav-item"
                                    style={{ marginTop: '0.5rem', justifyContent: 'center' }}
                                    onClick={() => setUiFontPreference(localFont, { persist: true })}
                                >
                                    <i className="fas fa-save"></i>
                                    <span>Save font for site</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* User Profile Footer + level */}
                    <div className="sidebar-footer">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>
                                {userProfile?.username?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                                <div style={{ fontSize: '0.9rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                                    {userProfile?.username || 'User'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                    {isAdmin ? 'Administrator' : 'Student'}
                                    {(userProfile?.assessment?.finalLevel != null) && (
                                        <span className="sidebar-user-level"> · {userProfile.assessment.finalLevel}급</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;
